/**
 * paymentDetection — o interruptor "detectar pagamento automaticamente" e a
 * escolha entre QR **estático** e **dinâmico**. MÓDULO PURO.
 *
 * ─── O QUE ESTÁ EM JOGO ─────────────────────────────────────────────────────
 *
 *   ESTÁTICO   O QR é gerado no cliente a partir de `companies.pix_key`
 *              (`src/lib/pixQrCode.ts`). O dinheiro cai direto na conta
 *              bancária do prestador e **o app nunca fica sabendo** — não há
 *              baixa automática, não há aviso, não há repasse. É o
 *              comportamento de sempre, e continua sendo o padrão.
 *
 *   DINÂMICO   O QR vem de uma cobrança criada na subconta Asaas do prestador
 *              (`asaas-charge`). O dinheiro passa pela subconta, o webhook
 *              avisa, o app dá baixa no financeiro com a data do CLIENTE e
 *              repassa o líquido para a chave de repasse.
 *
 * ─── POR QUE UM INTERRUPTOR SÓ, E NÃO DOIS CAMPOS ───────────────────────────
 *
 * "Detectar automaticamente" e "QR dinâmico" não são escolhas independentes: a
 * conciliação EXIGE que o dinheiro passe pela subconta, e é isso que o QR
 * dinâmico faz. A combinação "detecta + estático" é impossível — o dinheiro vai
 * direto para o banco do prestador e não há o que detectar. Dois campos
 * permitiriam gravar esse estado, e a tela prometeria uma baixa que nunca vem.
 *
 * Por isso o banco guarda UM booleano (`payment_settings.auto_detection_enabled`)
 * e o modo é DERIVADO aqui. Guardar o modo criaria a segunda fonte de verdade
 * que a trilha de trabalho (#121) evitou: bastaria a subconta ser reprovada
 * para a coluna dizer "dinâmico" enquanto nenhuma cobrança consegue nascer.
 *
 * ─── A REGRA QUE QUEBRA EM SILÊNCIO SE FOR IGNORADA ─────────────────────────
 *
 * O estado DESCONHECIDO cai em ESTÁTICO, nunca em dinâmico. Offline, com a
 * consulta ainda carregando, ou com a subconta em análise, o app não sabe se
 * consegue cobrar — e a única falha segura é imprimir o QR de sempre, que
 * funciona sem servidor nenhum. Falhar para o lado do dinâmico produziria um
 * documento SEM forma de pagamento (a cobrança não existe), e o cliente ficaria
 * sem como pagar sem que ninguém visse um erro.
 *
 * É o mesmo raciocínio de `acceptsPix` (#73): a ausência de informação repete o
 * passado, ela não inventa um comportamento novo.
 */

/** Espelha o enum `public.asaas_account_status`. */
export type AsaasAccountStatus =
  | 'pending'
  | 'awaiting_documents'
  | 'approved'
  | 'rejected'
  | 'disabled';

export type PixQrMode = 'static' | 'dynamic';

/**
 * Por que o modo é esse. Existe para a tela poder DIZER — um interruptor ligado
 * que continua imprimindo QR estático, sem explicação, lê como bug do app.
 */
export type PixQrModeReason =
  /** O interruptor está desligado (o padrão, e o estado de todo mundo hoje). */
  | 'disabled'
  /** O plano/flag não libera a feature. Vem ANTES de tudo: é o portão externo. */
  | 'feature_blocked'
  /** Ligado, mas a empresa nunca conectou uma subconta. */
  | 'no_account'
  /**
   * Ligado e não conseguimos LER a subconta (offline, ou a consulta falhou).
   *
   * Balde próprio, e não `no_account`: os dois caem em estático, mas dizer
   * "falta conectar sua conta" para quem já conectou é uma mentira que manda o
   * usuário refazer um cadastro que existe. Mesma disciplina dos três estados de
   * `financeEntries` na trilha (#121) — `null` (não posso ver) e `[]` (não há)
   * não podem colapsar.
   */
  | 'account_unknown'
  /** Subconta criada, aguardando análise ou documentos. */
  | 'account_pending'
  /** Análise cadastral recusada. */
  | 'account_rejected'
  /** Subconta desativada. */
  | 'account_disabled'
  /** Aprovada, mas sem chave de repasse: dá para cobrar e não dá para devolver. */
  | 'no_payout_key'
  /**
   * Aprovada no CADASTRO e ainda sem chave Pix própria — logo, incapaz de gerar
   * QR. São eixos diferentes no Asaas e é o erro mais traiçoeiro da integração:
   * `accountStatus.general = APPROVED` chega em segundos, mas enquanto o
   * titular não ativa a conta o `POST /v3/pix/addressKeys` responde "A sua conta
   * ainda não está totalmente aprovada para utilizar o Pix". Sem este motivo, o
   * app dá a conta como pronta e TODA cobrança nasce sem código.
   */
  | 'no_pix_key'
  /** Tudo pronto — este é o único caso `dynamic`. */
  | 'ready';

export interface PaymentDetectionAccount {
  status: AsaasAccountStatus;
  /** `asaas_accounts.payout_pix_key` — o destino do repasse. */
  payoutPixKey?: string | null;
  /**
   * `asaas_accounts.pix_key_ready` — a subconta tem chave Pix própria e
   * consegue emitir QR. NÃO é derivável de `status` (ver `no_pix_key`).
   */
  pixKeyReady?: boolean | null;
}

export interface PaymentDetectionInputs {
  /**
   * `payment_settings.auto_detection_enabled`. `null`/`undefined` = sem linha na
   * tabela = nunca configurado = desligado.
   */
  enabled?: boolean | null;
  /** `useFeature('auto_payment_detection').allowed`. */
  featureAllowed?: boolean | null;
  /**
   * A subconta da empresa. `null` = não existe; `undefined` = ainda não sabemos
   * (consulta carregando, ou offline). Os dois caem em estático, mas com motivos
   * DIFERENTES — ver `account_unknown`.
   */
  account?: PaymentDetectionAccount | null;
}

export interface PixQrModeResolution {
  mode: PixQrMode;
  reason: PixQrModeReason;
  /** Atalho para `mode === 'dynamic'`, que é o que a maioria dos call-sites quer. */
  dynamic: boolean;
}

const resolution = (mode: PixQrMode, reason: PixQrModeReason): PixQrModeResolution => ({
  mode,
  reason,
  dynamic: mode === 'dynamic',
});

/**
 * ─── CONGELAMENTO DO BaaS: A CAMADA QUE FALTAVA ─────────────────────────────
 *
 * O congelamento do addendum do #165 fechou o REGISTRO (`free`/`pro` =
 * `blocked`), o SERVIDOR (402 antes de tocar no Asaas) e o AMBIENTE (503 sem as
 * variáveis). Ficou de fora a RESOLUÇÃO — e ela é a única das quatro que decide
 * o que vai ao PAPEL.
 *
 * O buraco, medido em produção (2026-08-08): `plan_type = 'admin'` libera a
 * feature por invariante do registro (#166), e a conta do desenvolvedor tinha
 * `auto_detection_enabled = true` sobrando do E2E de sandbox (#171) com a
 * subconta aprovada. Resultado: `resolvePixQrMode` devolvia `dynamic`,
 * `pixQrEligible` recusava o QR estático (correto — #167: no dinâmico a chave
 * pessoal não pode aparecer) e, como nenhuma cobrança pode nascer com o BaaS
 * congelado, **TODO orçamento saía sem forma de pagamento nenhuma**. Sem erro,
 * sem log, sem toast: o PDF simplesmente não tinha Pix.
 *
 * Enquanto o BaaS estiver congelado, nenhum caminho — inclusive os dois de
 * teste (`admin` e `feature_overrides`, #110) — pode resolver `dynamic`, porque
 * o dinâmico ali é um beco: ele TIRA o QR estático e não põe nada no lugar.
 *
 * Para exercitar a cobrança de novo, troque esta constante para `false` no seu
 * checkout — é explícito, é uma linha, e não depende de um `plan_type` que
 * alguém pode ter marcado meses atrás. Ela sai JUNTO com o `blocked` do
 * registro e com a lista do servidor, e o tripwire de `paymentDetection.test.ts`
 * cobra os três.
 */
export const AUTO_PAYMENT_DETECTION_FROZEN = true;

/**
 * A feature está alcançável AGORA? É `featureAllowed` (plano/override) **e** o
 * congelamento acima.
 *
 * Existe como função, e não como `&&` espalhado, porque são três consumidores —
 * a resolução do modo, a visibilidade do bloco e a permissão de mexer no
 * interruptor — e um deles esquecido reabre exatamente o defeito de 2026-08-08
 * numa camada diferente.
 */
export function autoDetectionReachable(featureAllowed: boolean | null | undefined): boolean {
  return Boolean(featureAllowed) && !AUTO_PAYMENT_DETECTION_FROZEN;
}

/**
 * O modo EFETIVO deste documento/empresa.
 *
 * A ordem dos portões é deliberada e segue o encadeamento do #38 (o portão que
 * o usuário não resolve pagando vem primeiro): a feature é o portão externo, o
 * interruptor é a escolha dele, e só depois vem o estado da subconta — que é
 * onde moram os motivos ACIONÁVEIS ("mande os documentos", "cadastre a chave").
 * Invertida, a tela diria "detecção desligada" para quem ligou o interruptor e
 * está só esperando a análise.
 */
export function resolvePixQrMode(input: PaymentDetectionInputs | null | undefined): PixQrModeResolution {
  if (!input?.featureAllowed) return resolution('static', 'feature_blocked');
  if (input.enabled !== true) return resolution('static', 'disabled');

  // Os dois caem em estático — não há como cobrar sem subconta confirmada, e o
  // documento precisa sair com o QR que funciona sem servidor nenhum. Mas o
  // MOTIVO é diferente, e é ele que a tela mostra.
  if (input.account === undefined) return resolution('static', 'account_unknown');
  const account = input.account;
  if (!account) return resolution('static', 'no_account');

  switch (account.status) {
    case 'rejected':
      return resolution('static', 'account_rejected');
    case 'disabled':
      return resolution('static', 'account_disabled');
    case 'pending':
    case 'awaiting_documents':
      return resolution('static', 'account_pending');
    case 'approved':
      break;
    default:
      // Status novo do provedor que este bundle não conhece. Não promove a
      // dinâmico: um valor desconhecido nunca pode LIBERAR cobrança.
      return resolution('static', 'account_pending');
  }

  // Sem chave Pix própria a subconta NÃO emite QR: a cobrança nasce e volta sem
  // código, e o orçamento sai sem forma de pagamento. Vem antes da chave de
  // repasse porque impede o passo anterior — não adianta poder devolver um
  // dinheiro que não há como receber.
  if (account.pixKeyReady !== true) return resolution('static', 'no_pix_key');

  // Aprovada mas sem chave de repasse: a cobrança até nasceria, mas o dinheiro
  // ficaria preso na subconta. É pior do que não cobrar — o prestador veria a
  // venda "paga" e o dinheiro em lugar nenhum —, então cai em estático com um
  // motivo que diz exatamente o que fazer.
  if (!(typeof account.payoutPixKey === 'string' && account.payoutPixKey.trim())) {
    return resolution('static', 'no_payout_key');
  }

  return resolution('dynamic', 'ready');
}

/**
 * O interruptor pode ser LIGADO agora?
 *
 * Separado de {@link resolvePixQrMode} porque as perguntas são diferentes: aqui
 * é sobre a permissão de mexer no controle (plano + papel), lá é sobre o que o
 * documento imprime. Um `admin` sem plano vê o interruptor com upsell; um
 * `member` não vê o bloco (portão de papel, resolvido pelo chamador com o mesmo
 * `canSeeFinance` do financeiro).
 */
export function canToggleAutoDetection(input: {
  featureAllowed?: boolean | null;
  canWrite?: boolean | null;
  isOnline?: boolean | null;
}): boolean {
  // Online-only de propósito: o interruptor grava configuração, não dado de
  // negócio, e não entra na fila offline (mesma decisão de `stock_settings` #91
  // e dos modelos de mensagens #35). O chamador avisa em vez de enfileirar.
  return Boolean(input.featureAllowed) && Boolean(input.canWrite) && input.isOnline !== false;
}

/**
 * O texto que a tela mostra para cada motivo. Fica no módulo puro, e não nas
 * páginas, pela mesma razão dos rótulos da trilha (#121): com o texto espalhado,
 * Configurações e o orçamento passam a explicar o mesmo estado de dois jeitos.
 */
export const PIX_QR_MODE_HINTS: Record<PixQrModeReason, string> = {
  feature_blocked: 'Disponível no plano PRO.',
  disabled: 'O orçamento leva o QR da sua chave Pix, como sempre. Você confere e dá baixa manualmente.',
  no_account: 'Falta conectar sua conta de recebimento para o app conseguir identificar os pagamentos.',
  account_unknown: 'Não deu para conferir sua conta de recebimento agora. Enquanto isso, o orçamento sai com o QR da sua chave Pix.',
  account_pending: 'Sua conta de recebimento está em análise. Até liberar, o orçamento continua com o QR da sua chave Pix.',
  account_rejected: 'A análise da sua conta de recebimento não foi aprovada. Revise seus dados para tentar de novo.',
  account_disabled: 'Sua conta de recebimento está desativada. Fale com o suporte para reativar.',
  no_payout_key: 'Falta informar a chave Pix que vai receber o repasse. Sem ela o dinheiro ficaria retido.',
  no_pix_key: 'Sua conta de recebimento ainda não foi ativada para usar o Pix. Confira o e-mail de ativação que o banco enviou.',
  ready: 'O orçamento leva um QR exclusivo. Assim que o cliente pagar, o app dá baixa e avisa você.',
};
